import { CliError, EXIT_USAGE } from "../../../common.js";
import {
  findEpisodeV4,
  listActionLocationsV4,
  listSceneLocationsV4,
} from "../../script/v4/address.js";
import type {
  ActionV4,
  EpisodeV4,
  ExtensionMap,
  SceneV4,
  ScriptV4,
  SpeakerV4,
  StateV4,
} from "../../script/v4/types.js";
import type { JsonValue } from "../../script/shared/types.js";
import { parseV4Selector } from "../../script/v4/selector.js";
import { resolveV4Ordinal } from "../../script/v4/ordinal.js";

export type V4AuthoringExportKind =
  | "script"
  | "episodes"
  | "episode-spans"
  | "scenes"
  | "scene-spans"
  | "action-spans";

function blocked(message: string, received: string[]): never {
  throw new CliError(message, {
    exitCode: EXIT_USAGE,
    errorCode: "AUTHORING_SELECTOR_INVALID",
    data: { actual: received },
  });
}

function escapeHeader(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\|/g, "\\|").replace(/\]/g, "\\]").replace(/\n/g, "\\n");
}

function escapeContent(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\n/g, "\\n");
}

function record(type: string, slots: string[], content = ""): string {
  const header = [type, ...slots].map(escapeHeader).join("|");
  const body = escapeContent(content);
  return `[${header}]${body ? ` ${body}` : ""}`;
}

function canonicalJson(value: JsonValue): JsonValue {
  if (Array.isArray(value)) return value.map(canonicalJson);
  if (value !== null && typeof value === "object") {
    const result: Record<string, JsonValue> = {};
    for (const key of Object.keys(value).sort()) result[key] = canonicalJson(value[key]!);
    return result;
  }
  return value;
}

function extensionLines(extend: ExtensionMap | undefined): string[] {
  if (extend === undefined) return [];
  return [
    record("extensions", []),
    ...Object.keys(extend).sort().map((key) =>
      record("extend", [key], JSON.stringify(canonicalJson(extend[key]!)))),
  ];
}

function speakerSlot(speaker: SpeakerV4): string {
  if ("actor_id" in speaker) {
    return speaker.state_id === undefined ? speaker.actor_id : `${speaker.actor_id}/${speaker.state_id}`;
  }
  return speaker.label === undefined ? speaker.kind : `${speaker.kind}:${speaker.label}`;
}

function actionLines(action: ActionV4): string[] {
  const lines = extensionLines(action.extend);
  if (action.transition_prompt !== undefined) {
    lines.push(record("prompt", [
      action.transition_prompt.target,
      action.transition_prompt.process,
      action.transition_prompt.contrast,
    ]));
  }
  if (action.type === "dialogue" || action.type === "inner_thought") {
    const slots = [action.action_id, speakerSlot(action.speaker)];
    if ("parenthetical" in action) slots.push(action.parenthetical ?? "");
    lines.push(record(action.type === "dialogue" ? "dlg" : "think", slots, action.content));
  } else {
    lines.push(record(action.type === "action" ? "act" : "trans", [action.action_id], action.content));
  }
  return lines;
}

function sceneLines(scene: SceneV4, attrs: string): string[] {
  const lines = [...extensionLines(scene.extend), `<scene ${attrs}>`];
  if (scene.environment !== undefined) {
    const slots = scene.environment.time === undefined
      ? scene.environment.space === undefined ? [] : [scene.environment.space]
      : [scene.environment.space ?? "", scene.environment.time];
    lines.push(record("environment", slots));
  }
  lines.push(scene.location === null
    ? record("location", [])
    : record("location", [scene.location.location_id, ...scene.location.state_ids]));
  for (const ref of scene.actors) lines.push(record("cast", [ref.actor_id, ...ref.state_ids]));
  for (const ref of scene.props) lines.push(record("prop", [ref.prop_id, ...ref.state_ids]));
  lines.push("", "<body>");
  scene.actions.forEach((action) => lines.push(...actionLines(action)));
  lines.push("</body>", "", "</scene>");
  return lines;
}

function episodeLines(episode: EpisodeV4): string[] {
  const lines = [...extensionLines(episode.extend), `<episode id=${episode.episode_id}>`];
  if (episode.title !== undefined) lines.push(record("title", [], episode.title));
  if (episode.synopsis !== undefined) lines.push(record("synopsis", [], episode.synopsis));
  episode.scenes.forEach((scene, index) => {
    if (index > 0 || episode.title !== undefined || episode.synopsis !== undefined) lines.push("");
    lines.push(...sceneLines(scene, `id=${scene.scene_id}`));
  });
  lines.push("</episode>");
  return lines;
}

function stateLines(ownerId: string, state: StateV4): string[] {
  return [...extensionLines(state.extend), record("state", [ownerId, state.state_id, state.state_name], state.description)];
}

function declarationLines(script: ScriptV4): string[] {
  const lines = ["<create>"];
  for (const actor of script.actors) {
    lines.push(...extensionLines(actor.extend));
    if (actor.aliases !== undefined) {
      lines.push(record("aliases", []));
      actor.aliases.forEach((alias) => lines.push(record("alias", [], alias)));
    }
    const slots = [actor.actor_id, actor.actor_name, actor.importance];
    if (actor.role_type !== undefined) slots.push(actor.role_type);
    lines.push(record("actor", slots, actor.description));
    actor.states.forEach((state) => lines.push(...stateLines(actor.actor_id, state)));
  }
  for (const location of script.locations) {
    lines.push(...extensionLines(location.extend));
    if (location.aliases !== undefined) {
      lines.push(record("aliases", []));
      location.aliases.forEach((alias) => lines.push(record("alias", [], alias)));
    }
    lines.push(record("location", [location.location_id, location.location_name, location.importance], location.description));
    location.states.forEach((state) => lines.push(...stateLines(location.location_id, state)));
  }
  for (const prop of script.props) {
    lines.push(...extensionLines(prop.extend));
    if (prop.aliases !== undefined) {
      lines.push(record("aliases", []));
      prop.aliases.forEach((alias) => lines.push(record("alias", [], alias)));
    }
    lines.push(record("prop", [prop.prop_id, prop.prop_name, prop.importance], prop.description));
    prop.states.forEach((state) => lines.push(...stateLines(prop.prop_id, state)));
  }
  lines.push("</create>");
  return lines;
}

function scriptLines(script: ScriptV4): string[] {
  const lines = [...extensionLines(script.extend), "<script>"];
  lines.push(record("title", [], script.title), record("worldview", [], script.worldview));
  if (script.worldview_raw !== undefined) lines.push(record("worldview-raw", [], script.worldview_raw));
  lines.push(record("style", [], script.style));
  if (script.synopsis !== undefined) lines.push(record("synopsis", [], script.synopsis));
  if (script.logline !== undefined) lines.push(record("logline", [], script.logline));
  if (script.theme !== undefined) lines.push(record("theme", [], script.theme));
  if (script.main_characters !== undefined) {
    lines.push(record("main-characters", []));
    script.main_characters.forEach((item) => lines.push(record("main-character", [item.name, item.role])));
  }
  script.episodes.forEach((episode) => lines.push("", ...episodeLines(episode)));
  lines.push("</script>");
  return lines;
}

type Region = { left: string; right: string };

function regions(raw: string): Region[] {
  if (!raw.trim()) blocked("This export kind requires --in.", ["<missing>"]);
  return raw.split(",").map((part) => {
    const pieces = part.trim().split("..");
    if (pieces.length > 2 || !pieces[0] || (pieces.length === 2 && !pieces[1])) {
      return blocked("Selector region is invalid.", [part]);
    }
    return { left: pieces[0]!, right: pieces[1] ?? pieces[0]! };
  });
}

function orderedSelections<T>(
  script: ScriptV4,
  kind: "episode" | "scene" | "action",
  items: readonly T[],
  ids: readonly string[],
  rawSelector: string,
): T[][] {
  let previousEnd = -1;
  return regions(rawSelector).map((region) => {
    const resolveEndpoint = (raw: string): string => {
      if (!raw.startsWith("@")) return raw;
      const address = resolveV4Ordinal(script, raw);
      if (address.kind !== kind) {
        return blocked("Selector endpoint kind does not match the export kind.", [raw, kind]);
      }
      return address.id;
    };
    const left = ids.indexOf(resolveEndpoint(region.left));
    const right = ids.indexOf(resolveEndpoint(region.right));
    if (left < 0 || right < 0) blocked("Selector endpoint was not found.", [region.left, region.right]);
    const start = Math.min(left, right);
    const end = Math.max(left, right);
    if (start <= previousEnd) {
      blocked("Selector regions must follow current document order without overlap.", [
        `${region.left}..${region.right}`,
      ]);
    }
    previousEnd = end;
    return items.slice(start, end + 1);
  });
}

export function serializeAuthoringV4(
  script: ScriptV4,
  kind: V4AuthoringExportKind,
  rawSelector: string,
): { content: string; targetCount: number } {
  const lines = ["<authoring version=1>", ""];
  let targetCount = 0;
  if (kind === "script") {
    lines.push(...declarationLines(script), "", ...scriptLines(script));
    targetCount = 1;
  } else if (kind === "episodes") {
    const selector = parseV4Selector(script, rawSelector, { allowOrdinals: true });
    const episodes = script.episodes.filter((episode) => selector.matchesEpisode(episode.episode_id));
    episodes.forEach((episode, index) => {
      if (index > 0) lines.push("");
      lines.push(...episodeLines(episode));
    });
    targetCount = episodes.length;
  } else if (kind === "episode-spans") {
    const selections = orderedSelections(
      script,
      "episode",
      script.episodes,
      script.episodes.map((item) => item.episode_id),
      rawSelector,
    );
    for (const [index, episodes] of selections.entries()) {
      if (index > 0) lines.push("");
      lines.push(`<episode-span range=${episodes[0]!.episode_id}..${episodes.at(-1)!.episode_id}>`);
      episodes.forEach((episode) => lines.push(...episodeLines(episode)));
      lines.push("</episode-span>");
      targetCount++;
    }
  } else if (kind === "scenes") {
    const selector = parseV4Selector(script, rawSelector, { allowOrdinals: true });
    const rows = listSceneLocationsV4(script).filter((row) => selector.matchesScene(row.scene.scene_id));
    rows.forEach((row, index) => {
      if (index > 0) lines.push("");
      lines.push(...sceneLines(row.scene, `id=${row.scene.scene_id} parent=${row.episode.episode_id}`));
    });
    targetCount = rows.length;
  } else if (kind === "scene-spans") {
    const rows = listSceneLocationsV4(script);
    const selections = orderedSelections(
      script,
      "scene",
      rows,
      rows.map((row) => row.scene.scene_id),
      rawSelector,
    );
    for (const [index, selected] of selections.entries()) {
      const parent = selected[0]!.episode.episode_id;
      if (!selected.every((row) => row.episode.episode_id === parent)) {
        blocked("A scene span must remain inside one episode.", [
          selected[0]!.scene.scene_id,
          selected.at(-1)!.scene.scene_id,
        ]);
      }
      if (index > 0) lines.push("");
      lines.push(`<scene-span parent=${parent} range=${selected[0]!.scene.scene_id}..${selected.at(-1)!.scene.scene_id}>`);
      selected.forEach((row) => lines.push(...sceneLines(row.scene, `id=${row.scene.scene_id}`)));
      lines.push("</scene-span>");
      targetCount++;
    }
  } else {
    const rows = listActionLocationsV4(script);
    const selections = orderedSelections(
      script,
      "action",
      rows,
      rows.map((row) => row.action.action_id),
      rawSelector,
    );
    for (const [index, selected] of selections.entries()) {
      const parent = selected[0]!.scene.scene_id;
      if (!selected.every((row) => row.scene.scene_id === parent)) {
        blocked("An action span must remain inside one scene.", [
          selected[0]!.action.action_id,
          selected.at(-1)!.action.action_id,
        ]);
      }
      if (index > 0) lines.push("");
      lines.push(`<action-span parent=${parent} range=${selected[0]!.action.action_id}..${selected.at(-1)!.action.action_id}>`, "<body>");
      selected.forEach((row) => lines.push(...actionLines(row.action)));
      lines.push("</body>", "</action-span>");
      targetCount++;
    }
  }
  if (targetCount === 0) {
    blocked("Selector did not match any authoring targets.", [rawSelector || "<empty>"]);
  }
  lines.push("", "</authoring>");
  return { content: `${lines.join("\n")}\n`, targetCount };
}
